import React, { useState, useEffect } from 'react';
import { analyticsService } from '../../services/analyticsService';
import { SalesDashboardData, SalesAnalyticsFilters } from '../../types/analytics';
import MetricCard from '../Common/MetricCard';
import LineChart from '../Charts/LineChart';
import BarChart from '../Charts/BarChart';

interface SalesMetricsDashboardProps {
  filters: SalesAnalyticsFilters;
  onFiltersChange: (filters: SalesAnalyticsFilters) => void;
}

const SalesMetricsDashboard: React.FC<SalesMetricsDashboardProps> = ({ 
  filters, 
  onFiltersChange 
}) => {
  const [dashboardData, setDashboardData] = useState<SalesDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, [filters]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await analyticsService.getSalesDashboardData(filters);
      
      if (response.success && response.data) {
        setDashboardData(response.data);
      } else {
        setError(response.error?.message || 'Failed to load dashboard data');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (period: 'daily' | 'weekly' | 'monthly' | 'yearly') => {
    const dates = analyticsService.calculatePeriodDates(period);
    onFiltersChange({
      ...filters,
      period,
      startDate: dates.current.start,
      endDate: dates.current.end
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading data</h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
            <button
              onClick={loadDashboardData}
              className="mt-2 text-sm text-red-800 underline hover:text-red-900"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  const { summary, revenueData, periodComparison } = dashboardData;

  return (
    <div className="space-y-6">
      {/* Period Selection */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">Sales Analytics</h2>
          <div className="flex space-x-2">
            {(['daily', 'weekly', 'monthly', 'yearly'] as const).map((period) => (
              <button
                key={period}
                onClick={() => handlePeriodChange(period)}
                className={`px-3 py-1 text-sm rounded-md ${
                  filters.period === period
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Revenue"
          value={analyticsService.formatCurrency(summary.totalRevenue)}
          change={{
            value: analyticsService.calculatePercentageChange(
              summary.totalRevenue,
              periodComparison.previous.revenue
            ),
            type: summary.totalRevenue >= periodComparison.previous.revenue ? 'increase' : 'decrease'
          }}
          color="green"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
          }
        />

        <MetricCard
          title="Total Bookings"
          value={summary.totalBookings.toString()}
          change={{
            value: analyticsService.calculatePercentageChange(
              summary.totalBookings,
              periodComparison.previous.bookings
            ),
            type: summary.totalBookings >= periodComparison.previous.bookings ? 'increase' : 'decrease'
          }}
          color="blue"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
        />

        <MetricCard
          title="Average Booking Value"
          value={analyticsService.formatCurrency(summary.averageBookingValue)}
          change={{
            value: analyticsService.calculatePercentageChange(
              summary.averageBookingValue,
              periodComparison.previous.averageValue
            ),
            type: summary.averageBookingValue >= periodComparison.previous.averageValue ? 'increase' : 'decrease'
          }}
          color="purple"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          }
        />

        <MetricCard
          title="Conversion Rate"
          value={analyticsService.formatPercentage(summary.conversionRate / 100)}
          change={{
            value: 0, // This would need to be calculated from previous period data
            type: 'increase'
          }}
          color="yellow"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
      </div>

      {/* Revenue Trend Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <LineChart
          title="Revenue & Bookings Trend"
          data={revenueData.map(item => ({
            date: new Date(item.date).toLocaleDateString(),
            revenue: item.revenue,
            bookings: item.bookings
          }))}
          xAxisKey="date"
          lines={[
            { key: 'revenue', color: '#10B981', name: 'Revenue ($)' },
            { key: 'bookings', color: '#3B82F6', name: 'Bookings' }
          ]}
          height={350}
        />

        <BarChart
          title="Average Booking Value Trend"
          data={revenueData.map(item => ({
            date: new Date(item.date).toLocaleDateString(),
            averageValue: item.averageValue
          }))}
          xAxisKey="date"
          bars={[
            { key: 'averageValue', color: '#8B5CF6', name: 'Avg Value ($)' }
          ]}
          height={350}
        />
      </div>

      {/* Period Comparison */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Period-over-Period Comparison</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {analyticsService.formatCurrency(periodComparison.current.revenue)}
            </div>
            <div className="text-sm text-gray-500">Current Period Revenue</div>
            <div className={`text-sm font-medium ${
              periodComparison.changes.revenue >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {periodComparison.changes.revenue >= 0 ? '+' : ''}
              {analyticsService.formatPercentage(periodComparison.changes.revenue / 100)}
            </div>
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {periodComparison.current.bookings}
            </div>
            <div className="text-sm text-gray-500">Current Period Bookings</div>
            <div className={`text-sm font-medium ${
              periodComparison.changes.bookings >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {periodComparison.changes.bookings >= 0 ? '+' : ''}
              {analyticsService.formatPercentage(periodComparison.changes.bookings / 100)}
            </div>
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {analyticsService.formatCurrency(periodComparison.current.averageValue)}
            </div>
            <div className="text-sm text-gray-500">Current Avg Booking Value</div>
            <div className={`text-sm font-medium ${
              periodComparison.changes.averageValue >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {periodComparison.changes.averageValue >= 0 ? '+' : ''}
              {analyticsService.formatPercentage(periodComparison.changes.averageValue / 100)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesMetricsDashboard;